import Collaboration from '@tiptap/extension-collaboration'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import { EditorContent, useEditor } from '@tiptap/react'
import { HocuspocusProvider } from '@hocuspocus/provider'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import { noteExtensions } from '@collab/schema'
import { mentionSuggestion } from './mentionSuggestion.ts'
import { useUsers } from '../../hooks/useTasks.ts'
import { userColorVar } from '../shell/Avatar.tsx'
import type { User } from '../../lib/types.ts'
import styles from './notes.module.css'

export type SyncState = 'syncing' | 'saved'

/**
 * The value still lives in tokens.css and nowhere else (RULES §5) — this only reads it, because the
 * caret colour has to travel over the wire to the other person's browser as an actual colour.
 */
function resolveColor(variable: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim()
}

/**
 * One Y.Doc and one provider per open note, torn down on leaving the view (PRD §7).
 *
 * There is no Save button and no conflict dialog — CRDT merges, so neither concept exists here.
 * Do not add them (PRD §4.3).
 */
export function NoteEditor({
  noteId,
  me,
  onSyncChange,
  onTypingChange,
}: {
  noteId: number
  me: User
  onSyncChange: (state: SyncState) => void
  onTypingChange: (names: { name: string; colorVar: string }[]) => void
}): React.JSX.Element {
  // Recreated per note: a Y.Doc belongs to exactly one document.
  const ydoc = useMemo(() => new Y.Doc(), [noteId])
  const [provider, setProvider] = useState<HocuspocusProvider | null>(null)

  // Kept in a ref so the provider is not rebuilt every time the parent re-renders.
  const syncRef = useRef(onSyncChange)
  syncRef.current = onSyncChange

  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'

    /**
     * "synchronizuję" vs "zapisano" — the only save indicator in the product (ui-spec §5.4).
     * Synced alone is not enough: local edits can be pending while the connection is fine, and
     * saying "zapisano" then would be a lie.
     */
    const report = (p: HocuspocusProvider): void => {
      syncRef.current(p.isSynced && !p.hasUnsyncedChanges ? 'saved' : 'syncing')
    }

    const next: HocuspocusProvider = new HocuspocusProvider({
      url: `${proto}://${window.location.host}/collab`,
      name: `note:${noteId}`,
      document: ydoc,
      // Auth rides on the session cookie in the handshake — the browser cannot read an httpOnly
      // cookie to pass it here, and that is the point of the flag (collab/hocuspocus.ts).
      onSynced: () => report(next),
      onStatus: () => report(next),
      onUnsyncedChanges: () => report(next),
      onDisconnect: () => syncRef.current('syncing'),
    })

    setProvider(next)
    return () => {
      next.destroy()
      setProvider(null)
    }
  }, [noteId, ydoc])

  // Who else has their cursor in this note right now.
  useEffect(() => {
    if (!provider) return
    const update = (): void => {
      const states = [...provider.awareness!.getStates().entries()]
      const others = states
        .filter(([clientId]) => clientId !== provider.awareness!.clientID)
        .map(([, state]) => (state as { user?: { name: string; colorVar: string } }).user)
        .filter((u): u is { name: string; colorVar: string } => Boolean(u))
      onTypingChange(others)
    }
    update()
    provider.awareness?.on('change', update)
    return () => {
      provider.awareness?.off('change', update)
    }
  }, [provider, onTypingChange])

  // Read through a ref so the suggestion config can stay stable: rebuilding it would remount the
  // editor, and remounting an editor bound to a live Y.Doc is how you lose what somebody just typed.
  const users = useUsers()
  const usersRef = useRef<User[]>([])
  usersRef.current = users.data ?? []
  const suggestion = useMemo(() => mentionSuggestion(() => usersRef.current), [])

  const editor = useEditor(
    {
      extensions: [
        ...noteExtensions({ suggestion }),
        Collaboration.configure({ document: ydoc }),
        ...(provider
          ? [
              CollaborationCaret.configure({
                provider,
                user: {
                  name: me.displayName,
                  // Resolved to a literal colour, not the `var(...)` itself: Tiptap parses this
                  // value and rejects a CSS variable ("unsupported color format"), which left every
                  // caret falling back to the same stylesheet default — two people, one colour.
                  color: resolveColor(userColorVar(me.id)),
                  colorVar: userColorVar(me.id),
                },
              }),
            ]
          : []),
      ],
      editorProps: {
        attributes: { 'data-placeholder': 'Zacznij pisać…' },
      },
    },
    [ydoc, provider],
  )

  return (
    <div className={styles.editor}>
      <EditorContent editor={editor} />
    </div>
  )
}
